import React, { useState, useEffect, useCallback } from "react";
import { IoDocumentText, IoDownload, IoRefresh, IoSearch } from "react-icons/io5";
import { fetchReportsFromApi, downloadReport, filterReports } from "../utils/azureReports";
import Loader from "./reusableComponents/loader";
import { useStores } from "../stores";
import ReactPaginate from "react-paginate";

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
};

export default function Reports() {
  const { clientsStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 15;

  // Fetch reports function - memoized with useCallback
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedReports = await fetchReportsFromApi();
      setReports(fetchedReports);
      setFilteredReports(fetchedReports);
    } catch (err) {
      console.error("Error fetching reports:", err);
      setError(
        err?.message || "Failed to fetch reports. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch reports on component mount
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Filter reports when search term changes
  useEffect(() => {
    const filtered = filterReports(reports, searchTerm);
    setFilteredReports(filtered);
    setCurrentPage(0); // Reset to first page when search changes
  }, [searchTerm, reports]);

  const handleDownload = async (report) => {
    try {
      setLoading(true);
      await downloadReport(report.downloadUrl, report.name);
    } catch (err) {
      console.error("Error downloading report:", err);
      alert("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Calculate pagination
  const pageCount = Math.ceil(filteredReports.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = filteredReports.slice(startIndex, endIndex);

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="w-full h-screen overflow-y-auto bg-gray-50 p-6">
      <Loader loader={loading} text="Loading reports..." />
      
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <IoDocumentText className="text-blue-600" />
              Reports
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Download client reports from Azure storage
            </p>
          </div>
          <button
            onClick={fetchReports}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            disabled={loading}
          >
            <IoRefresh className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <IoSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client name, plan type, or date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Stats */}
        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-800">{filteredReports.length}</span>
            <span>Reports Found</span>
          </div>
          {searchTerm && (
            <div className="flex items-center gap-2">
              <span>•</span>
              <button
                onClick={() => setSearchTerm("")}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Clear Search
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Reports Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredReports.length === 0 && !loading ? (
          <div className="text-center py-12">
            <IoDocumentText className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">
              {searchTerm ? "No reports match your search" : "No reports available"}
            </p>
            <p className="text-gray-400 text-sm">
              {searchTerm
                ? "Try adjusting your search criteria"
                : "Reports will appear here once they are uploaded to Azure"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Report Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Size
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedReports.map((report, index) => (
                    <tr key={report.blobName} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {report.clientName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {clientsStore.camelToTitleCase(report.planType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {report.reportDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatFileSize(report.size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleDownload(report)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                          title="Download Report"
                        >
                          <IoDownload />
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pageCount > 1 && (
              <div className="border-t border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredReports.length)} of{" "}
                    {filteredReports.length} reports
                  </div>
                  <ReactPaginate
                    nextLabel=">"
                    previousLabel="<"
                    onPageChange={handlePageChange}
                    forcePage={currentPage}
                    pageRangeDisplayed={2}
                    marginPagesDisplayed={1}
                    pageCount={pageCount}
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link px-3 py-2"
                    containerClassName="flex items-center gap-1"
                    pageClassName="page-item"
                    pageLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors border border-gray-300"
                    previousClassName="page-item"
                    previousLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors border border-gray-300"
                    nextClassName="page-item"
                    nextLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors border border-gray-300"
                    activeClassName="active"
                    activeLinkClassName="bg-blue-500 text-white hover:bg-blue-600 font-semibold border-blue-500"
                    disabledClassName="opacity-50 cursor-not-allowed"
                    disabledLinkClassName="cursor-not-allowed"
                    renderOnZeroPageCount={null}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
